import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'

/**
 * A button that waits on the network and SAYS SO THE INSTANT IT IS PRESSED.
 *
 * ★★ THE FEEDBACK LANDS ON THE CLICK, NOT ON THE RESPONSE. Sign-out takes about three seconds
 * against the API and used to show nothing at all while it worked (David, 2026-07-23: "it needs to
 * show a spinner to indicate that something is happening"). A control that does not acknowledge a
 * press reads as broken, and the natural response is to press it again — which is exactly what you
 * do not want on an action that is already in flight.
 *
 * ⚠ IT ALSO GUARDS AGAINST THE SECOND CLICK. Disabled while in flight, so a double press cannot
 * fire the request twice.
 *
 * ⚠ AND IT SURVIVES A FAILURE. If the promise rejects, the button comes back rather than sitting
 * spinning forever — a spinner that never resolves is a worse lie than no spinner at all.
 */
export function BusyButton({
  onClick,
  children,
  busyLabel,
  className,
  style,
  type = 'button',
  title,
}: {
  onClick: () => void | Promise<unknown>
  children: ReactNode
  /** What to say while it is working. Defaults to the idle label. */
  busyLabel?: ReactNode
  className?: string
  style?: CSSProperties
  type?: 'button' | 'submit'
  title?: string
}) {
  const [busy, setBusy] = useState(false)

  // ⚠ On success the component is usually unmounted by the navigation the click caused, so the
  // state update that follows would land on a dead component. Track liveness rather than let React
  // warn — and rather than skip the reset, which would strand a still-mounted button.
  const alive = useRef(true)
  useEffect(() => () => {
    alive.current = false
  }, [])

  const run = useCallback(() => {
    if (busy) return
    setBusy(true)
    Promise.resolve(onClick())
      .catch(() => undefined)
      .finally(() => {
        if (alive.current) setBusy(false)
      })
  }, [busy, onClick])

  return (
    <button
      className={className}
      type={type}
      title={title}
      style={{ ...style, opacity: busy ? 0.75 : 1, cursor: busy ? 'progress' : style?.cursor ?? 'pointer' }}
      disabled={busy}
      aria-busy={busy}
      onClick={type === 'submit' ? undefined : run}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        {busy && <span className="dash-spinner" aria-hidden="true" />}
        {busy ? busyLabel ?? children : children}
      </span>
    </button>
  )
}
